import { forwardRef, ReactNode } from "react";
import { InputNumber, InputNumberProps, Space } from "antd";

type InputNumberRef = React.ComponentRef<typeof InputNumber>;

export type InputNumberWithAddonProps = Omit<
  InputNumberProps<number>,
  "addonBefore" | "addonAfter"
> & {
  addonBefore?: ReactNode;
  addonAfter?: ReactNode;
};

/**
 * Replacement for antd's deprecated `InputNumber` `addonBefore` / `addonAfter`
 * props. Renders the same `Space.Compact` + `Space.Addon` structure antd v6 used
 * internally for those props, while keeping a single child so it can sit
 * directly inside a named `Form.Item` (value / onChange / id are forwarded to
 * the inner `InputNumber`). As before, `className` / `style` land on the input
 * and `rootClassName` on the compact wrapper.
 */
const InputNumberWithAddon = forwardRef<
  InputNumberRef,
  InputNumberWithAddonProps
>(({ addonBefore, addonAfter, rootClassName, ...rest }, ref) => {
  const renderAddon = (node: ReactNode) =>
    node ? (
      <Space.Addon
        variant={rest.variant}
        disabled={rest.disabled}
        status={rest.status}
      >
        {node}
      </Space.Addon>
    ) : null;

  return (
    <Space.Compact rootClassName={rootClassName}>
      {renderAddon(addonBefore)}
      <InputNumber<number> ref={ref} {...rest} />
      {renderAddon(addonAfter)}
    </Space.Compact>
  );
});

InputNumberWithAddon.displayName = "InputNumberWithAddon";

export default InputNumberWithAddon;
